import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api/client';
import { keys } from '@/lib/query/keys';
import { useAuth } from '@/lib/auth/useAuth';
import { COLLECTIONS } from '@/config/collections';
import { getLeadType } from '@/config/leadTypes';
import { DataView } from '@/lib/ui/states';
import { Card } from '@/lib/ui/Card';
import { SpecBand } from '@/lib/ui/SpecBand';
import { PageHeader, Eyebrow, MonoValue } from '@/lib/ui/Typography';
import type { DashboardSummary } from '@/lib/api/types';
import { formatBytes, formatRelative } from '@/lib/format';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The dashboard `content` map keys come from the backend with underscores (e.g.
 * `product_families`, `job_postings`). Pages and product families are NOT generic content
 * collections — they have dedicated routes — so they are special-cased here. Everything else
 * resolves through the collection registry; unknown keys render as non-navigable cards.
 */
function collectionRoute(col: string): string | null {
  if (col === 'pages') return '/pages';
  if (col === 'product_families' || col === 'product-families') return '/families';
  const key = col.replace(/_/g, '-');
  return COLLECTIONS.some((c) => c.key === key) ? `/content/${key}` : null;
}

function collectionLabel(col: string, t: TFn): string {
  if (col === 'pages') return t('nav.pages');
  if (col === 'product_families' || col === 'product-families') return t('nav.productFamilies');
  const key = col.replace(/_/g, '-');
  const cfg = COLLECTIONS.find((c) => c.key === key);
  return cfg ? t(cfg.label, { defaultValue: cfg.label }) : col.replace(/_/g, ' ');
}

/** Localize an audit action/entity token, falling back to a humanized form. */
function localizeToken(kind: 'action' | 'entity', token: string, t: TFn): string {
  if (!token) return '';
  return t(`activity.${kind}.${token}`, { defaultValue: token.replace(/_/g, ' ') });
}

/** Dashboard (spec §8). Renders defensively (iterate keys). Sales sees a leads-only variant. */
export function DashboardPage() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const query = useQuery({
    queryKey: keys.dashboard(),
    queryFn: () => api.get<DashboardSummary>('/admin/dashboard'),
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
  const salesOnly = role === 'sales';

  return (
    <div>
      <PageHeader eyebrow={t('dashboard.eyebrow')} title={t('dashboard.title')} />
      <DataView query={query}>
        {(d) => (
          <div className="flex flex-col gap-lg">
            {!salesOnly && (
              <>
                <section>
                  <Eyebrow>{t('dashboard.pages')}</Eyebrow>
                  <SpecBand
                    className="mt-sm"
                    items={[
                      { label: t('dashboard.total'), value: <MonoValue>{d.pages?.total ?? 0}</MonoValue> },
                      { label: t('dashboard.published'), value: <MonoValue>{d.pages?.published ?? 0}</MonoValue> },
                      { label: t('dashboard.draft'), value: <MonoValue>{d.pages?.draft ?? 0}</MonoValue> },
                      {
                        label: t('dashboard.media'),
                        value: <MonoValue>{d.media?.total_assets ?? 0}</MonoValue>,
                        hint: formatBytes(d.media?.total_size_bytes ?? 0),
                      },
                    ]}
                  />
                </section>

                <section>
                  <Eyebrow>{t('dashboard.contentByCollection')}</Eyebrow>
                  <div className="mt-sm grid gap-md sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(d.content ?? {}).map(([col, c]) => {
                      const route = collectionRoute(col);
                      const count: { total?: number; published?: number } =
                        c && typeof c === 'object' ? c : {};
                      const card = (
                        <Card interactive={!!route}>
                          <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
                            {collectionLabel(col, t)}
                          </p>
                          <p className="mt-xs text-title text-ink-deep">
                            <MonoValue>{count.published ?? 0}</MonoValue>
                            {typeof count.total === 'number' && (
                              <span className="text-small text-ink-mute">
                                {' '}
                                {t('dashboard.ofPublished', { total: count.total })}
                              </span>
                            )}
                          </p>
                        </Card>
                      );
                      return route ? (
                        <Link key={col} to={route}>
                          {card}
                        </Link>
                      ) : (
                        <div key={col}>{card}</div>
                      );
                    })}
                  </div>
                </section>
              </>
            )}

            <section>
              <Eyebrow>{t('dashboard.leads')}</Eyebrow>
              <div className="mt-sm grid gap-md sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(d.leads ?? {}).map(([type, l]) => {
                  // Defend against the value not being an object (legacy flat-int shape).
                  const lead: { total?: number; new?: number } =
                    l && typeof l === 'object' ? l : {};
                  const cfg = getLeadType(type);
                  const newCount = lead.new ?? 0;
                  // Newsletter has no "new" pipeline; its "new" counts pending double-opt-in.
                  const route = cfg
                    ? type === 'newsletter'
                      ? `/leads/${type}?status=pending_confirmation`
                      : `/leads/${type}?status=new`
                    : null;
                  const label = cfg ? t(`nav.${cfg.navKey}`) : type.replace(/_/g, ' ');
                  const card = (
                    <Card interactive={!!route}>
                      <div className="flex items-center justify-between">
                        <p className="font-mono text-label-sm uppercase tracking-[0.12em] text-ink-mute">
                          {label}
                        </p>
                        {newCount > 0 && (
                          <span className="rounded-pill bg-accent-blue px-[8px] py-[2px] font-mono text-label text-canvas-paper">
                            {t('dashboard.newCount', { count: newCount })}
                          </span>
                        )}
                      </div>
                      <p className="mt-xs text-title text-ink-deep">
                        <MonoValue>{lead.total ?? 0}</MonoValue>
                        <span className="text-small text-ink-mute"> {t('dashboard.totalSuffix')}</span>
                      </p>
                    </Card>
                  );
                  return route ? (
                    <Link key={type} to={route}>
                      {card}
                    </Link>
                  ) : (
                    <div key={type}>{card}</div>
                  );
                })}
              </div>
            </section>

            {d.recent_activity && d.recent_activity.length > 0 && (
              <section>
                <Eyebrow>{t('dashboard.recentActivity')}</Eyebrow>
                <Card className="mt-sm p-0">
                  <ul className="divide-y divide-hairline">
                    {d.recent_activity.slice(0, 12).map((a, i) => (
                      <li
                        key={a.id ?? `${a.created_at}-${i}`}
                        className="flex items-center justify-between px-lg py-sm text-small"
                      >
                        <span>
                          <span className="font-medium text-ink-deep">{a.actor_email ?? t('dashboard.systemActor')}</span>{' '}
                          <span className="text-ink-mute">{localizeToken('action', a.action, t)}</span>{' '}
                          <span className="text-ink-mid">{localizeToken('entity', a.entity_type, t)}</span>
                        </span>
                        <span className="font-mono text-label-sm text-ink-mute">
                          {formatRelative(a.created_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </section>
            )}
          </div>
        )}
      </DataView>
    </div>
  );
}
